import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: 'too-expensive', label: 'Too expensive for my budget' },
  { value: 'not-using', label: "I'm not using it enough" },
  { value: 'missing-features', label: 'Missing features I need' },
  { value: 'found-alternative', label: 'Found another tool I prefer' },
  { value: 'other', label: 'Other (tell us below)' },
];

/**
 * Cancellation feedback modal. Shows BEFORE the actual cancellation call so we
 * can capture the reason. If the teacher dismisses the modal we don't cancel.
 *
 * Props:
 *  - open / onOpenChange    — controlled dialog state
 *  - onConfirmedCancel()    — fired AFTER the reason is saved; parent then calls /subscription/cancel
 */
export default function CancellationFeedbackModal({ open, onOpenChange, onConfirmedCancel }) {
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason(null);
    setDetail('');
    setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      // Record reason. We deliberately swallow errors so a flaky network can't
      // trap a teacher who's trying to cancel — they still get to cancel.
      await axios.post(`${API}/cancellation-feedback`, { reason, detail }).catch(() => {});
      handleClose(false);
      onConfirmedCancel();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="cancellation-modal">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl text-[#14213D]">
            Before you go &mdash;
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5A6577] pt-1">
            What&apos;s the main reason you&apos;re cancelling? Your answer helps us improve Atlas History.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {REASONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setReason(r.value)}
              className={`w-full text-left text-sm px-4 py-2.5 rounded-md border transition-colors ${
                reason === r.value
                  ? 'bg-[#14213D] text-[#FDFBF7] border-[#14213D]'
                  : 'bg-white text-[#2D3748] border-[#EBE7E0] hover:border-[#14213D]/30'
              }`}
              data-testid={`cancel-reason-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {(reason === 'other' || reason === 'missing-features') && (
          <Textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder={reason === 'missing-features'
              ? 'Which features would have made you stay?'
              : 'Tell us more (optional)'}
            rows={3}
            maxLength={500}
            className="text-sm"
            data-testid="cancel-detail-input"
          />
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            data-testid="cancel-keep-subscription-btn"
          >
            Never mind, keep my plan
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className="bg-[#9B2C2C] hover:bg-[#7A1F1F] text-[#FDFBF7]"
            data-testid="cancel-confirm-btn"
          >
            {submitting ? 'Cancelling…' : 'Submit & Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
